/**
 * Verdiva Theme - Product Form JavaScript
 *
 * Handles:
 * - Variant selection
 * - Add to cart
 * - Price updates
 * - Inventory status
 */

(function() {
  'use strict';

  class ProductForm {
    constructor(container) {
      this.container = container;
      this.form = container.querySelector('[data-product-form]');
      this.productData = this.getProductData();

      if (!this.form || !this.productData) return;

      this.variantInput = this.form.querySelector('[data-variant-id]');
      this.addToCartBtn = this.form.querySelector('[data-add-to-cart]');
      // De prijs bovenaan de pagina (sections/product-info.liquid). Zonder deze
      // koppeling bleef bij een maatwissel de oude prijs staan, terwijl de
      // totaalprijs in de koopmodule wel meebewoog.
      this.priceElement = container.querySelector('[data-price]');
      this.comparePriceElement = container.querySelector('.product-info__price-compare');
      this.quantityInput = container.querySelector('[data-quantity-input]');

      this.currentVariant = this.productData.variants.find(v => v.id === parseInt(this.variantInput?.value));

      this.bindEvents();
    }

    getProductData() {
      // Het JSON-blok staat in sections/product-info.liquid buiten .product-info,
      // dus zoeken we het desnoods in het hele document. Zonder die terugval
      // startte deze klasse helemaal niet op en werkte de variantkeuze niet.
      const dataEl =
        this.container.querySelector('[data-product-json]') ||
        document.querySelector('[data-product-json]');
      if (!dataEl) return null;

      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('Error parsing product data:', e);
        return null;
      }
    }

    bindEvents() {
      // Variant option buttons
      this.container.querySelectorAll('.product-info__variant-option').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleOptionChange(e));
      });

      // Form submission
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));

      // De plus/min-knoppen zijn van assets/product-configurator.js; hier ook
      // luisteren zou het aantal per klik dubbel laten oplopen.
    }

    handleOptionChange(e) {
      const btn = e.currentTarget;
      const optionName = btn.dataset.optionName;
      const optionValue = btn.dataset.optionValue;

      // Update selected state
      const group = btn.closest('.product-info__variant-group');
      group.querySelectorAll('.product-info__variant-option').forEach(b => {
        b.classList.remove('selected');
      });
      btn.classList.add('selected');

      // Find matching variant
      const selectedOptions = this.getSelectedOptions();
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.currentVariant = variant;
        this.updateVariant(variant);
      }
    }

    getSelectedOptions() {
      const options = {};
      this.container.querySelectorAll('.product-info__variant-group').forEach(group => {
        const selectedBtn = group.querySelector('.product-info__variant-option.selected');
        if (selectedBtn) {
          options[selectedBtn.dataset.optionName] = selectedBtn.dataset.optionValue;
        }
      });
      return options;
    }

    findVariant(selectedOptions) {
      return this.productData.variants.find(variant => {
        return this.productData.options.every((option, index) => {
          const optionValue = selectedOptions[option];
          return variant.options[index] === optionValue;
        });
      });
    }

    updateVariant(variant) {
      // Update hidden input
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Update price
      this.updatePrice(variant);

      // Update add to cart button
      this.updateAddToCartButton(variant);

      // Update unavailable options
      this.updateUnavailableOptions();
    }

    updatePrice(variant) {
      if (this.priceElement) {
        this.priceElement.textContent = this.formatMoney(variant.price);
      }

      if (this.comparePriceElement) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceElement.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceElement.style.display = '';
        } else {
          this.comparePriceElement.style.display = 'none';
        }
      }
    }

    updateAddToCartButton(variant) {
      if (!this.addToCartBtn) return;

      this.addToCartBtn.disabled = !variant.available;

      // Alleen het tekstlabel aanpassen, zodat het winkelwagen-icoon in de knop
      // blijft staan. Het oorspronkelijke label onthouden we bij de eerste keer.
      const label = this.addToCartBtn.querySelector('[data-add-label]');
      if (!label) return;

      if (!label.dataset.defaultText) {
        label.dataset.defaultText = label.textContent.trim();
      }

      label.textContent = variant.available
        ? label.dataset.defaultText
        : (window.theme?.strings?.soldOut || 'Uitverkocht');
    }

    updateUnavailableOptions() {
      // Mark unavailable variant combinations
      // This is a simplified version - full implementation would check all combinations
      this.container.querySelectorAll('.product-info__variant-option').forEach(btn => {
        btn.classList.remove('unavailable');
      });
    }

    updateQuantity(change) {
      if (!this.quantityInput) return;

      const currentValue = parseInt(this.quantityInput.value) || 1;
      const newValue = Math.max(1, currentValue + change);
      this.quantityInput.value = newValue;
    }

    async handleSubmit(e) {
      // Variant opnieuw bepalen op basis van het verborgen id-veld, voor het
      // geval de selectie-state niet meer klopt.
      if (!this.currentVariant) {
        this.currentVariant = this.productData.variants.find(
          v => v.id === parseInt(this.variantInput?.value)
        );
      }

      // Kunnen we nog steeds geen variant bepalen? Blokkeer de submit dan NIET.
      // Laat de browser het formulier normaal versturen (native POST naar
      // /cart/add), zodat het product alsnog wordt toegevoegd in plaats van dat
      // de knop niets doet.
      if (!this.currentVariant) return;

      // Variant is uitverkocht: niets toevoegen, maar ook geen page-reload.
      if (!this.currentVariant.available) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      const quantity = parseInt(this.quantityInput?.value) || 1;

      // Alleen het label binnen de knop aanpassen, zodat het icoon blijft staan.
      const labelEl = this.addToCartBtn.querySelector('[data-add-label]') || this.addToCartBtn;
      this.addToCartBtn.disabled = true;
      const originalText = labelEl.textContent;
      labelEl.textContent = 'Bezig…';

      try {
        const response = await fetch(window.theme?.routes?.cart_add_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: this.currentVariant.id,
            quantity: quantity
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Update cart count
        if (window.Verdiva?.updateCartCount) {
          await window.Verdiva.updateCartCount();
        }

        // Open cart drawer
        if (window.cartDrawer) {
          window.cartDrawer.open();
        }

        labelEl.textContent = 'Toegevoegd';

        setTimeout(() => {
          labelEl.textContent = originalText;
          this.addToCartBtn.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('Error adding to cart:', error);
        labelEl.textContent = window.theme?.strings?.cartError || 'Er ging iets mis';

        setTimeout(() => {
          labelEl.textContent = originalText;
          this.addToCartBtn.disabled = false;
        }, 2000);
      }
    }

    formatMoney(cents) {
      if (window.Verdiva?.formatMoney) {
        return window.Verdiva.formatMoney(cents);
      }

      const format = window.theme?.moneyFormat || '€{{amount}}';
      const value = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', value);
    }
  }

  // Initialize on product pages
  const init = () => {
    const productSection = document.querySelector('.product-info');
    if (productSection) {
      new ProductForm(productSection);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.ProductForm = ProductForm;

})();
